import { FileImage, FileText, Mic, Sticker, Video, MapPin, User } from "lucide-react";

export const getMediaIconComponent = (mediaKind) => {
  const kind = (mediaKind || '').toLowerCase().trim();

  const iconMap = {
    'image': FileImage,
    'video': Video,
    'audio': Mic,
    'voice': Mic,
    'document': FileText,
    'sticker': Sticker,
    'location': MapPin,
    'contact': User
  };

  return iconMap[kind] || null;
};

// Channel icon shown at the avatar's bottom-right corner (replaces the old presence dot).
export const getChannelIconSrc = (channel) => {
  const ch = (channel || '').toString().toLowerCase();
  if (ch.includes('whatsapp')) return '/images/whatsapp icon.svg';
  if (ch.includes('telegram')) return '/images/telegram.svg';
  return null;
};

export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  
  const messageDate = new Date(timestamp);
  const now = new Date();
  
  // Check if the date is valid
  if (isNaN(messageDate.getTime())) return '';
  
  const diffInMs = now - messageDate;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  // For messages less than 1 minute old
  if (diffInMinutes < 1) {
    return 'now';
  }
  
  // For messages less than 1 hour old
  if (diffInMinutes < 60) {
    return `${diffInMinutes} min${diffInMinutes === 1 ? '' : 's'}`;
  }
  
  // For messages less than 24 hours old
  if (diffInHours < 24) {
    return `${diffInHours} hr${diffInHours === 1 ? '' : 's'}`;
  }
  
  // For messages from yesterday and up to a week old, show day name and time in 24-hour format
  if (diffInDays < 7) {
    return messageDate.toLocaleDateString('en-US', { 
      weekday: 'short',
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  }
  
  // For messages older than a week, show full date and time in 24-hour format
  return messageDate.toLocaleDateString('en-US', { 
    month: 'short',
    day: 'numeric',
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

/**
 * Formats time for chat list preview (shorter format)
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted time string for chat list
 */
export const formatChatTime = (timestamp) => {
  if (!timestamp) return '';
  
  const messageDate = new Date(timestamp);
  const now = new Date();
  
  if (isNaN(messageDate.getTime())) return '';
  
  const diffInMs = now - messageDate;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  // For messages less than 1 minute old
  if (diffInMinutes < 1) {
    return 'now';
  }
  
  // For messages less than 1 hour old
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  }
  
  // For messages less than 24 hours old
  if (diffInHours < 24) {
    return `${diffInHours}h`;
  }
  
  // For messages from yesterday
  if (diffInDays === 1) {
    return 'Yesterday';
  }
  
  // For messages older than yesterday, show date
  if (diffInDays < 7) {
    return messageDate.toLocaleDateString('en-US', { 
      weekday: 'short'
    });
  }
  
  // For messages older than a week, show date
  return messageDate.toLocaleDateString('en-US', { 
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formats time as HH:MM (24h) for chat list (e.g. 18:14)
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Time string in 00:00 format
 */
export const formatChatListTime = (timestamp) => {
  if (!timestamp) return '';
  const messageDate = new Date(timestamp);
  if (isNaN(messageDate.getTime())) return '';

  const now = new Date();
  const isToday =
    messageDate.getDate() === now.getDate() &&
    messageDate.getMonth() === now.getMonth() &&
    messageDate.getFullYear() === now.getFullYear();

  if (isToday) {
    return messageDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear();

  if (isYesterday) {
    return 'Yesterday';
  }

  const dd = String(messageDate.getDate()).padStart(2, '0');
  const mm = String(messageDate.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${messageDate.getFullYear()}`;
};

export const formatMessagePreview = (message) => {
  if (!message) return 'No messages';
  let cleanMessage = message
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/#{1,6}\s+/g, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleanMessage.length > 50 ? cleanMessage.substring(0, 50) + '...' : cleanMessage;
};

/** Short preview for chat list (truncate with "...") */
export const formatMessagePreviewShort = (message, maxLen = 24) => {
  if (!message) return '...';
  let cleanMessage = (typeof message === 'string' ? message : '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/#{1,6}\s+/g, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleanMessage) return '...';
  return cleanMessage.length > maxLen ? cleanMessage.substring(0, maxLen) + '...' : cleanMessage;
};

export const formatFileSize = (size) => {
  if (size > 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(size / 1024).toFixed(1)} KB`;
};

export const getFileIcon = (fileType, fileName, mediaKind = '') => {
  // Check media kind first for more accurate detection
  if (mediaKind === 'image' || fileType.startsWith('image/')) {
    return { icon: '🖼️', bgClass: 'bg-blue-100', textClass: 'text-blue-600' };
  }
  if (mediaKind === 'video' || fileType.startsWith('video/')) {
    return { icon: '🎥', bgClass: 'bg-purple-100', textClass: 'text-purple-600' };
  }
  if (mediaKind === 'audio' || mediaKind === 'voice' || fileType.startsWith('audio/')) {
    return { icon: '🎵', bgClass: 'bg-green-100', textClass: 'text-green-600' };
  }
  if (mediaKind === 'voice') {
    return { icon: '🎤', bgClass: 'bg-orange-100', textClass: 'text-orange-600' };
  }
  if (mediaKind === 'sticker') {
    return { icon: '😀', bgClass: 'bg-yellow-100', textClass: 'text-yellow-600' };
  }
  if (mediaKind === 'location') {
    return { icon: '📍', bgClass: 'bg-red-100', textClass: 'text-red-600' };
  }
  if (mediaKind === 'contact') {
    return { icon: '👤', bgClass: 'bg-indigo-100', textClass: 'text-indigo-600' };
  }
  if (fileType.includes('pdf')) {
    return { icon: '📄', bgClass: 'bg-red-100', textClass: 'text-red-600' };
  }
  if (fileType.includes('document') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
    return { icon: '📝', bgClass: 'bg-blue-100', textClass: 'text-blue-600' };
  }
  if (fileName.endsWith('.txt')) {
    return { icon: '📄', bgClass: 'bg-gray-100', textClass: 'text-gray-600' };
  }
  if (fileName.endsWith('.zip') || fileName.endsWith('.rar')) {
    return { icon: '📦', bgClass: 'bg-yellow-100', textClass: 'text-yellow-600' };
  }
  return { icon: '📎', bgClass: 'bg-gray-100', textClass: 'text-gray-600' };
};

export const getTimeUpdateInterval = (timestamp) => {
  if (!timestamp) return null;
  
  const messageDate = new Date(timestamp);
  const now = new Date();
  
  if (isNaN(messageDate.getTime())) return null;
  
  const diffInMs = now - messageDate;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  
  // Update every minute for messages less than 1 hour old
  if (diffInMinutes < 60) {
    return 60000; // 1 minute
  }
  
  // Update every hour for messages less than 24 hours old
  if (diffInHours < 24) {
    return 3600000; // 1 hour
  }
  
  // No updates needed for older messages
  return null;
};

export const getDateKey = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
};

export const formatDateLabel = (timestamp) => {
  if (!timestamp) return '';
  
  const messageDate = new Date(timestamp);
  const now = new Date();
  
  if (isNaN(messageDate.getTime())) return '';
  
  // Reset time to start of day for accurate date comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const diffInDays = Math.floor((today - messageDay) / (1000 * 60 * 60 * 24));
  
  // Today
  if (diffInDays === 0) {
    return 'Today';
  }
  
  // Yesterday
  if (diffInDays === 1) {
    return 'Yesterday';
  }
  
  // Within the last 7 days - show day name
  if (diffInDays < 7) {
    return messageDate.toLocaleDateString('en-US', { 
      weekday: 'long' 
    });
  }
  
  // Within the last 30 days - show day and month
  if (diffInDays < 30) {
    return messageDate.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  }
  
  // Older than 30 days - show full date
  return messageDate.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

export const formatSimpleDate = (timestamp) => {
  if (!timestamp) return '';
  
  const messageDate = new Date(timestamp);
  const now = new Date();
  
  if (isNaN(messageDate.getTime())) return '';
  
  // Reset time to start of day for accurate date comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
  
  const diffInDays = Math.floor((today - messageDay) / (1000 * 60 * 60 * 24));
  
  // Today
  if (diffInDays === 0) {
    return 'Today';
  }
  
  // Yesterday
  if (diffInDays === 1) {
    return 'Yesterday';
  }
  
  // Within the last 7 days - show day name
  if (diffInDays < 7) {
    return messageDate.toLocaleDateString('en-US', { 
      weekday: 'long' 
    });
  }
  
  // Older than 7 days - show MM/DD/YYYY format
  return messageDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const groupMessagesByDate = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];

  const grouped = {};

  messages.forEach(message => {
    const dateKey = getDateKey(message.timestamp);
    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        date: message.timestamp,
        dateKey,
        messages: []
      };
    }
    grouped[dateKey].messages.push(message);
  });

  // Convert to array and sort by date (oldest first)
  return Object.values(grouped).sort((a, b) => new Date(a.dateKey) - new Date(b.dateKey));
};

/**
 * Format last message display for chat items
 * Logic:
 * - If only last_msg_body: show text
 * - If only last_msg_media_kind: show media indicator
 * - If both: show caption/text with media indicator
 * - If both empty: show "..."
 */
export const formatChatLastMessage = (lastMsgBody = '', lastMsgMediaKind = '', maxLen = 24) => {
  const bodyExists = lastMsgBody && lastMsgBody.trim() !== '';
  const mediaExists = lastMsgMediaKind && lastMsgMediaKind.trim() !== '';

  if (!bodyExists && !mediaExists) {
    return '...';
  }

  const mediaKind = lastMsgMediaKind?.toLowerCase().trim() || '';

  // Get media indicator text based on media kind (returns text label only, icons rendered via Lucide)
  const getMediaIndicator = () => {
    if (!mediaExists) return '';

    const mediaLabelMap = {
      'image': 'Image',
      'video': 'Video',
      'audio': 'Audio',
      'voice': 'Voice message',
      'document': 'Document',
      'sticker': 'Sticker',
      'location': 'Location',
      'contact': 'Contact'
    };

    return mediaLabelMap[mediaKind] || mediaKind.charAt(0).toUpperCase() + mediaKind.slice(1);
  };

  // If only media, show media indicator
  if (!bodyExists && mediaExists) {
    return getMediaIndicator();
  }

  // If only body, show text
  if (bodyExists && !mediaExists) {
    let cleanMessage = (typeof lastMsgBody === 'string' ? lastMsgBody : '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .replace(/#{1,6}\s+/g, '')
      .replace(/\n+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleanMessage) return '...';
    return cleanMessage.length > maxLen ? cleanMessage.substring(0, maxLen) + '...' : cleanMessage;
  }

  // If both caption/body and media exist, show: "caption/text ... [media indicator]"
  let cleanMessage = (typeof lastMsgBody === 'string' ? lastMsgBody : '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/#{1,6}\s+/g, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleanMessage) {
    return getMediaIndicator();
  }

  // Truncate caption/body to leave room for media indicator (roughly)
  const mediaIndicator = getMediaIndicator();
  const combinedMaxLen = maxLen;
  let truncatedMessage = cleanMessage;

  if (cleanMessage.length > combinedMaxLen - 15) {
    truncatedMessage = cleanMessage.substring(0, combinedMaxLen - 15) + '...';
  }

  return `${truncatedMessage} [${mediaIndicator}]`;
};
